/**
 * Affiliate Dashboard Page
 * 
 * Protected route that shows the affiliate dashboard.
 * Only accessible to authenticated users who are active or suspended affiliates.
 * 
 * Route: /affiliate
 * 
 * Flow:
 * - Not authenticated → Show login prompt (NOT redirect - affiliate might just need to log in)
 * - Authenticated but not affiliate → redirect to /become-affiliate
 * - Authenticated + pending affiliate → redirect to /become-affiliate
 * - Authenticated + active/suspended affiliate → show dashboard
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { LoginModal } from "@/components/auth/login-modal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/spinner";
import { SparklesIcon, AlertTriangle } from "lucide-react";
import { useAffiliateData } from "./hooks/use-affiliate-data";
import { AffiliateHeaderCard, type AffiliateTab } from "./components/affiliate-header-card";
import { AffiliateOverviewCard } from "./components/affiliate-overview-card";
import { AffiliateLinksTable } from "./components/affiliate-links-table";
import { AffiliateLinkCreateModal } from "./components/affiliate-link-create-modal";
import { AffiliateLinkEditModal } from "./components/affiliate-link-edit-modal";
import { AffiliateLinkQrModal } from "./components/affiliate-link-qr-modal";
import { AffiliateEarningsChart } from "./components/affiliate-earnings-chart";
import { AffiliateActivityChart } from "./components/affiliate-activity-chart";
import { AffiliateReferralsTable } from "./components/affiliate-referrals-table";
import { AffiliatePayoutHistory } from "./components/affiliate-payout-history";
import { AffiliateSettingsTab } from "./components/affiliate-settings-tab";
import { type DateRangeValue } from "@/components/ui/date-range-selector";
import type { AffiliateLink } from "./types";

export const AffiliatePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isReady } = useUnifiedAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AffiliateTab>("overview");
  const [dateRange, setDateRange] = useState<DateRangeValue>("all");
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  
  // Link modal states
  const [createLinkModalOpen, setCreateLinkModalOpen] = useState(false);
  const [editLinkModalOpen, setEditLinkModalOpen] = useState(false);
  const [qrLinkModalOpen, setQrLinkModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<AffiliateLink | null>(null);

  const {
    affiliate,
    stats,
    referrals,
    payouts,
    earningsData,
    activityData,
    isLoading,
    isLoadingStats,
    isLoadingReferrals,
    isLoadingPayouts,
    isLoadingEarnings,
    isLoadingActivity,
  } = useAffiliateData(dateRange);

  // Sticky header on window scroll
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsHeaderSticky(scrollTop > 20);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Redirect logic - ONLY for authenticated users who aren't affiliates
  // Don't redirect unauthenticated users - they might be affiliates who just need to log in
  useEffect(() => {
    // Don't redirect until auth is ready
    if (!isReady) return;
    
    // Don't redirect unauthenticated users - show them login prompt instead
    if (!isAuthenticated) return;
    
    // Don't redirect until affiliate data has loaded
    if (isLoading) return;
    
    // Authenticated but no affiliate record or pending status → redirect to become-affiliate
    if (!affiliate || affiliate.status === "pending") {
      navigate("/become-affiliate", { replace: true });
      return;
    }
  }, [isReady, isAuthenticated, isLoading, affiliate, navigate]);

  // Show loading spinner while auth is initializing
  // This ensures authenticated users see spinner, not login prompt
  if (!isReady) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <Spinner />
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login prompt (NOT redirect!)
  // This allows existing affiliates to log in without being sent to the landing page
  if (!isAuthenticated) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-4">
            <SparklesIcon className="size-8 text-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Affiliate Dashboard</h2>
          <p className="text-muted-foreground mb-6">
            Log in to access your affiliate dashboard and track your earnings.
          </p>
          <Button onClick={() => setLoginModalOpen(true)} size="lg">
            Log In to Continue
          </Button>
          <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
        </div>
      </div>
    );
  }

  // Authenticated but loading affiliate data - show spinner
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <Spinner />
          <p className="text-muted-foreground mt-4">Loading affiliate data...</p>
        </div>
      </div>
    );
  }

  // If we get here without an affiliate or with pending status, we're about to redirect
  // Show spinner to avoid flash
  if (!affiliate || affiliate.status === "pending") {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <Spinner />
        </div>
      </div>
    );
  }

  // Active or Suspended affiliate - show dashboard
  // (Suspended affiliates can see dashboard but can't request payouts)
  return (
    <main className="flex-1 p-4 md:p-6">
      <div className="max-w-[1600px] mx-auto space-y-4">
        {/* Suspended Warning Banner */}
        {affiliate.status === "suspended" && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-rose-600">Affiliate Account Suspended</h4>
                <p className="text-sm text-rose-600/80 mt-1">
                  Your affiliate account is temporarily suspended. You cannot request payouts at this time. 
                  Commissions continue to accumulate and will be available when your account is reactivated. 
                  Please contact support for more information.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Header Card with Tabs */}
        <AffiliateHeaderCard
          affiliate={affiliate}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          isSticky={isHeaderSticky}
        />

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-4 md:space-y-6">
            {/* Row 1: Stats Grid (6 cards in 3-column layout) */}
            <AffiliateOverviewCard 
              stats={stats} 
              isLoading={isLoadingStats} 
              dateRange={dateRange}
            />

            {/* Row 2: Earnings Chart (50%) + Activity Chart (50%) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <AffiliateEarningsChart 
                data={earningsData} 
                isLoading={isLoadingEarnings}
                dateRange={dateRange}
              />
              <AffiliateActivityChart
                data={activityData}
                isLoading={isLoadingActivity}
                dateRange={dateRange}
              />
            </div>

            {/* Row 3: Referrals Table */}
            <AffiliateReferralsTable
              referrals={referrals.slice(0, 5)}
              isLoading={isLoadingReferrals}
            />
          </div>
        )}

        {/* Links Tab */}
        {activeTab === "links" && (
          <AffiliateLinksTable
            onCreateLink={() => setCreateLinkModalOpen(true)}
            onEditLink={(link) => {
              setSelectedLink(link);
              setEditLinkModalOpen(true);
            }}
            onShowQr={(link) => {
              setSelectedLink(link);
              setQrLinkModalOpen(true);
            }}
          />
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <AffiliateReferralsTable referrals={referrals} isLoading={isLoadingReferrals} />
        )}

        {/* Payouts Tab */}
        {activeTab === "payouts" && (
          <AffiliatePayoutHistory
            payouts={payouts}
            pendingBalance={stats?.pendingPayout || 0}
            isLoading={isLoadingPayouts}
            affiliateStatus={affiliate.status}
          />
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && <AffiliateSettingsTab affiliate={affiliate} />}
      </div>

      {/* Link Modals */}
      <AffiliateLinkCreateModal
        open={createLinkModalOpen}
        onOpenChange={setCreateLinkModalOpen}
        affiliate={affiliate}
      />
      <AffiliateLinkEditModal
        open={editLinkModalOpen}
        onOpenChange={setEditLinkModalOpen}
        link={selectedLink}
        affiliate={affiliate}
      />
      <AffiliateLinkQrModal
        open={qrLinkModalOpen}
        onOpenChange={setQrLinkModalOpen}
        link={selectedLink}
      />
    </main>
  );
};
